import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDataStoreService } from '../context/DataStoreContext';
import { UserContext } from '../context/UserContext';
import useProfileService from '../hooks/useProfileService';
import { UserProfile } from '../services/interfaces';
import { logger } from '../services/LoggerService';
import { AppRoutes } from '../routes';

export const useLogin = () => {
    const [error, setError] = useState<string>('');
    const [needsVerification, setNeedsVerification] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const { setUserProfile } = useContext(UserContext);
    const { tutorProfileService } = useDataStoreService();
    const { getProfile, saveProfile, removeProfile } = useProfileService();
    const authService = useAuth();
    const navigate = useNavigate();

    const getUserProfile = async (id: string): Promise<UserProfile> => {
        const localProfile = getProfile();
        if (localProfile) return localProfile;
        return await tutorProfileService.getTutorProfile(id);
    };

    const login = async (email: string, password: string) => {
        setLoading(true);
        setError('');
        try {
            const user = await authService.login(email, password);

            if (!user.emailVerified) {
                setNeedsVerification(true);
                // Logout in verify, need auth to send email
                navigate(AppRoutes.VERIFY);
            } else {
                const profile = await getUserProfile(user.uid);
                setUserProfile(profile);
                saveProfile(profile);
                navigate(AppRoutes.TUTOR_PROFILE);
            }
        } catch (error: any) {
            logger.error('Login failed:', error);
            setError(`Login failed: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            await authService.logout();
            removeProfile();
            navigate(AppRoutes.LOGIN);
        } catch (error: any) {
            logger.error('Issue logging out', error);
            alert(`Issue logging out: ${error.message}`);
        }
    };

    return {
        error,
        setError,
        needsVerification,
        loading,
        login,
        logout
    };
};
